import React, { useEffect, useMemo } from "react"
import styles from "@css/modules/Listing.module.scss"
import { Popover, Skeleton, Tooltip } from "antd"
import { ArrowDown } from "@css/icons/SVG"
import { getFromTo, titleMap, usePointChart } from "./utils"
import cn from "classnames"
import { Controls } from "./components/Controls"
import { QuestionIco } from "@css/icons/QuestionIco"
import Link from "next/link"
import ExampleImage from "./point-summary-example.png"
import Image from "next/image"
import { useGeneralData } from "../../GeneralData/GeneralData"

function Progress({ value, className }) {
    return (
        <div className={cn(styles.progress, className)}>
            <div style={{ transform: `translateX(${value}%)` }} className={styles.progressBar} />
        </div>
    )
}

const renderSeasons = (periods, period, onChange) => {
    return (
        <div className={styles.seasonsPopoverContent}>
            {periods.map((periodItem) => {
                const selected = periodItem.group === period
                return (
                    <div key={periodItem.group} onClick={() => onChange(periodItem.group)} className={styles.item} data-selected={selected}>
                        <div className={styles.title}>Travel period {periodItem.group}</div>
                        {periodItem.item.map((i) => (
                            <div key={i.date} className={styles.subItem}>
                                {i.date}
                            </div>
                        ))}
                    </div>
                )
            })}
        </div>
    )
}
/**
 * Function for rendering a points summary with title, points, resort, resorts, readyChart, and classes.
 *
 * @param {object} [title] - The title of the points summary
 * @param {number} points - The points for the summary
 * @param {object} resort - The selected resort object
 * @param {object} chartData - server data
 * @param {object} [classes] - The custom classes for styling
 * @return {JSX.Element} The rendered points summary component
 */
export function PointsSummary({ title, points, resort, chartData, classes }) {
    const { resorts } = useGeneralData()

    const { loading, periods, rows, onChange, period, popoverOpen, togglePeriodsPopup, ...rest } = usePointChart(resort, points, chartData)
    useEffect(() => {
        rest.onChangeResort(resort.id)
    }, [resort.id])
    useEffect(() => {
        rest.changePoints(points)
    }, [points])
    const selectedResort = useMemo(() => {
        return resorts.find((i) => i.id === rest.resortId)
    }, [rest.resortId, resorts])
    const titleText = title ? <h2>{title}</h2> : <Controls resortName={resort.short_name} resorts={resorts} {...rest} />
    return (
        <div className={cn(styles.pointsSummary, classes?.root)}>
            <div className={cn(styles.pointsSummaryHeader, classes?.header)}>
                {titleText}
                {!title && (
                    <Popover
                        placement="bottom"
                        onOpenChange={togglePeriodsPopup}
                        open={popoverOpen}
                        overlayClassName={cn(styles.pointsSummaryPopover)}
                        content={() => renderSeasons(periods, period, onChange)}
                        trigger="click">
                        <button className={styles.pointsSummaryBtn}>
                            Travel period {period} <ArrowDown />
                        </button>
                    </Popover>
                )}
            </div>
            {loading ? (
                <Skeleton className={styles.pointsSummarySkeleton} round active />
            ) : (
                <table>
                    <thead className={styles.hiddenTableHeader}>
                        <tr>
                            <th>Room type</th>
                            <th>Amount of night progress</th>
                            <th>Amount of nights</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((i) => {
                            //  const className = i.maxDays === 0 ? styles.pointsSummaryEmpty : undefined
                            return (
                                <tr key={i.title}>
                                    <td className={styles.coll1}>{titleMap[i.title] || i.title}</td>
                                    <td>
                                        <Progress value={i.progress} />
                                    </td>
                                    <td className={styles.pointsSummaryNights}>{getFromTo(i)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}

            {!title && (
                <Tooltip
                    overlayClassName={styles.howDoWeCalculateTooltip}
                    title={
                        <>
                            <Image src={ExampleImage} alt={"Point summary example"} width={568} />
                            <hr />
                            Room categories have varying point requirements for nightly stays. Staying in a lower-category room allows you to use your points for more nights
                            compared to higher-category rooms.
                            <br />
                            <Link href={`/resorts/${selectedResort?.slug}/chart`} className={styles.goToResortChart}>
                                Resort Point Chart
                            </Link>
                        </>
                    }>
                    <div className={styles.howDoWeCalculate}>
                        How do we calculate nights-per-year? <QuestionIco />
                    </div>
                </Tooltip>
            )}
        </div>
    )
}
